import { openDB, type DBSchema } from 'idb';
import { api } from './api.js';

interface OutboxItem {
  id?: number;
  idempotencyKey: string;
  path: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body: unknown;
  createdAt: string;
  attempts: number;
  lastError?: string;
}

interface CachedWorkout {
  id: 'active';
  value: unknown;
  updatedAt: string;
}

interface RahulDatabase extends DBSchema {
  outbox: {
    key: number;
    value: OutboxItem;
    indexes: { 'by-created': string; 'by-key': string };
  };
  state: { key: string; value: CachedWorkout };
}

const database = openDB<RahulDatabase>('rahul365', 1, {
  upgrade(db) {
    const outbox = db.createObjectStore('outbox', { keyPath: 'id', autoIncrement: true });
    outbox.createIndex('by-created', 'createdAt');
    outbox.createIndex('by-key', 'idempotencyKey', { unique: true });
    db.createObjectStore('state', { keyPath: 'id' });
  },
});

export async function queueMutation(item: Omit<OutboxItem, 'id' | 'attempts' | 'createdAt'>): Promise<void> {
  const db = await database;
  const duplicate = await db.getFromIndex('outbox', 'by-key', item.idempotencyKey);
  if (duplicate) return;
  await db.add('outbox', { ...item, attempts: 0, createdAt: new Date().toISOString() });
  window.dispatchEvent(new CustomEvent('rahul365:outbox-change'));
}

export async function sendOrQueue<T>(item: Omit<OutboxItem, 'id' | 'attempts' | 'createdAt'>): Promise<{ queued: boolean; data?: T }> {
  if (!navigator.onLine) {
    await queueMutation(item);
    return { queued: true };
  }
  try {
    return {
      queued: false,
      data: await api<T>(item.path, { method: item.method, body: JSON.stringify(item.body) }),
    };
  } catch (error) {
    if (error instanceof TypeError) {
      await queueMutation(item);
      return { queued: true };
    }
    throw error;
  }
}

let activeFlush: Promise<number> | null = null;

async function flushQueuedItems(): Promise<number> {
  const db = await database;
  const items = await db.getAllFromIndex('outbox', 'by-created');
  let completed = 0;
  for (const item of items) {
    if (!navigator.onLine) break;
    try {
      await api(item.path, { method: item.method, body: JSON.stringify(item.body) });
      if (item.id !== undefined) await db.delete('outbox', item.id);
      completed += 1;
    } catch (error) {
      await db.put('outbox', {
        ...item,
        attempts: item.attempts + 1,
        lastError: error instanceof Error ? error.message : 'Sync failed',
      });
      break;
    }
  }
  window.dispatchEvent(new CustomEvent('rahul365:outbox-change'));
  return completed;
}

export function flushOutbox(): Promise<number> {
  if (!navigator.onLine) return Promise.resolve(0);
  if (activeFlush) return activeFlush;
  activeFlush = flushQueuedItems().finally(() => { activeFlush = null; });
  return activeFlush;
}

export async function outboxCount(): Promise<number> {
  return (await database).count('outbox');
}

export async function saveActiveWorkout(value: unknown): Promise<void> {
  await (await database).put('state', { id: 'active', value, updatedAt: new Date().toISOString() });
}

export async function loadActiveWorkout<T>(): Promise<T | null> {
  const record = await (await database).get('state', 'active');
  return (record?.value as T | undefined) ?? null;
}

export async function clearActiveWorkout(): Promise<void> {
  await (await database).delete('state', 'active');
}
